import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { IStringFormControlConfig } from '@app/core/interfaces/form-control-config';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { TextareaModule } from 'primeng/textarea';
import { SvgIconComponent } from 'angular-svg-icon';
import { NgClass } from '@angular/common';

@Component({
  selector: 'gc-comment-form',
  standalone: true,
  imports: [
    NgClass,
    ButtonModule,
    TranslateDirective,
    TranslatePipe,
    SelectModule,
    MessageModule,
    TextareaModule,
    ButtonModule,
    ReactiveFormsModule,
    SvgIconComponent,
  ],
  templateUrl: './comment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  public maxCommentsLength = 1000;

  public isEditMode = signal(false);

  public isFocused = signal(false);

  public facade = input.required<IStringFormControlConfig>();

  public showSuccessMessage = input<boolean>(false);

  private facade$ = toObservable(this.facade);

  public formGroup?: FormGroup<{
    comment: FormControl<string | null>;
  }>;

  public showSuccessMessageNotNeeded = signal<boolean>(false);
  public lastSavedValue = signal<string | null | undefined>(null);

  public isEditVisible = computed(() => this.isEditMode() || !this.lastSavedValue());

  constructor() {
    effect(() => {
      const isEditVisible = this.isEditVisible();
      const control = this.formGroup?.controls['comment'];

      if (!control) {
        return;
      }

      if (isEditVisible && control.disabled) {
        control.enable({ emitEvent: false });
      } else if (!isEditVisible && control.enabled) {
        control.disable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.facade$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.initComponent();
    });
  }

  private initComponent() {
    this.formGroup = new FormGroup({
      comment: new FormControl<string | null>(null, [
        Validators.required,
        Validators.maxLength(this.maxCommentsLength),
      ]),
    });

    this.initSubscriptions();
  }

  private initSubscriptions() {
    const facade = this.facade();

    facade
      .getValue()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.lastSavedValue.set(value);
        this.formGroup?.controls['comment'].setValue(value || facade.defaultValue, {
          emitEvent: false,
        });

        if (!value) {
          this.activateEditView();
        }
      });
  }

  public activateEditView() {
    this.isEditMode.set(true);
    this.showSuccessMessageNotNeeded.set(true);
  }

  public cancel() {
    const facade = this.facade();

    facade.getValue().subscribe(value => {
      this.formGroup?.controls['comment'].setValue(value || facade.defaultValue, {
        emitEvent: false,
      });

      this.isEditMode.set(false);
    });
  }

  public submit() {
    this.showSuccessMessageNotNeeded.set(false);
    const value = this.formGroup?.controls['comment'].value;
    const facade = this.facade();

    facade.valueChange(value || facade.defaultValue);
    this.isEditMode.set(false);

    queueMicrotask(() => this.cdr.detectChanges());
  }
}
